import React, { useState, useEffect } from 'react';
import { WifiOff, CheckCircle2 } from 'lucide-react';
import { triggerToast } from './ToastNotification';

export const NetworkStatusIndicator: React.FC = () => {
  const [isOnline, setIsOnline] = useState<boolean>(() => 
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [showReconnected, setShowReconnected] = useState<boolean>(false);

  useEffect(() => {
    let reconnectTimer: NodeJS.Timeout;

    const handleOnline = () => {
      setIsOnline(true);
      setShowReconnected(true);
      
      triggerToast({
        title: 'Back Online 🌐',
        message: 'Connection restored. All your study progress is synchronized.',
        type: 'success',
        duration: 4000
      });

      reconnectTimer = setTimeout(() => {
        setShowReconnected(false);
      }, 3500);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowReconnected(false);

      triggerToast({
        title: 'Offline Mode Active 📡',
        message: 'You are currently studying offline. Progress and quiz logs are saved locally.',
        type: 'info',
        duration: 5000
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, []);

  if (!isOnline) {
    return (
      <div 
        id="network-status-indicator"
        role="status"
        aria-live="polite"
        title="Offline Mode: Your study session is active offline. Progress is saved locally and will sync when reconnected."
        className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/15 text-amber-800 dark:text-amber-300 border border-amber-500/30 rounded-full text-[11px] font-bold tracking-wide transition-all animate-fadeIn shrink-0 select-none"
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
        </span>
        <WifiOff size={13} className="text-amber-600 dark:text-amber-400 shrink-0" />
        <span className="hidden sm:inline font-semibold">Offline Mode</span>
        <span className="sm:hidden font-semibold">Offline</span>
      </div>
    );
  }

  if (showReconnected) {
    return (
      <div 
        id="network-status-indicator"
        role="status"
        aria-live="polite"
        title="Connection restored. Study progress synchronized."
        className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 border border-emerald-500/30 rounded-full text-[11px] font-bold tracking-wide transition-all animate-fadeIn shrink-0 select-none"
      >
        <span className="inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
        <CheckCircle2 size={13} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
        <span className="hidden sm:inline font-semibold">Back Online</span>
        <span className="sm:hidden font-semibold">Online</span>
      </div>
    );
  }

  return null;
};
